import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_SETTING_PC,
  DEFAULT_SETTING_REFLECTION,
  DEFAULT_SETTING_WORKFLOW_STOP_AFTER,
  SettingsResponsePC,
  SettingsResponseReflection,
  SettingsResponseWorkflowStopAfter,
} from "@pages/data-management/data-management-types";
import { FaroTextButton } from "@components/common/faro-text-button";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Divider, Stack } from "@mui/material";
import { getPublishSettings, setPublishSettings } from "@pages/data-management/data-management-utils";
import { PublishButton } from "@pages/data-management/publish-button";
import { PublishOptions } from "@pages/data-management/publish-options";
import { captureTreeForOpenDraftRevisionSelector } from "@store/capture-tree/capture-tree-selectors";
import { useAppSelector } from "@store/store-helper";
import { sphereColors } from "@styles/common-colors";
import { getEllipsisLines, withEllipsisThreeLines } from "@styles/common-styles";
import { summarizeRevisionChanges } from "@utils/capture-tree/capture-tree-changes";
import { useSettingsApiClient } from "@api/use-settings-api-client";

interface Props extends BaseProjectIdProps {
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * The panel shown for publishing and re-publishing.
 * Left side: "Ready to publish!" rectangle.
 * Right side: Checkboxes for publish options + List of unpublished changes.
 */
export function PublishPanel({ projectId, setIsOpen }: Props): JSX.Element {
  const { isScreenXsAndSmall } = useMediaQueryList();
  const openDraftEntities = useAppSelector(captureTreeForOpenDraftRevisionSelector);
  const settingsApiClient = useSettingsApiClient();
  const { handleErrorWithToast } = useErrorContext();

  const [settingPC, setSettingPC] = useState<SettingsResponsePC>({
    // eslint-disable-next-line @typescript-eslint/naming-convention -- external package
    value: DEFAULT_SETTING_PC,
  } as SettingsResponsePC);
  const [settingReflection, setSettingReflection] = useState<SettingsResponseReflection>({
    // eslint-disable-next-line @typescript-eslint/naming-convention -- external package
    value: DEFAULT_SETTING_REFLECTION,
  } as SettingsResponseReflection);
  const [settingWorkflowStopAfter, setSettingWorkflowStopAfter] = useState<SettingsResponseWorkflowStopAfter>({
    value: DEFAULT_SETTING_WORKFLOW_STOP_AFTER,
  } as SettingsResponseWorkflowStopAfter);

  useEffect(() => {
    (async () => {
      const { settingPC, settingReflection, settingWorkflowStopAfter } = await getPublishSettings(projectId, settingsApiClient);
      setSettingPC(settingPC);
      setSettingReflection(settingReflection);
      // (re-)publish button should always do "publish" so do not set non-default value for settingWorkflowStopAfter!
      settingWorkflowStopAfter.value = DEFAULT_SETTING_WORKFLOW_STOP_AFTER;
      setSettingWorkflowStopAfter(settingWorkflowStopAfter);
    })().catch(() => undefined);
  }, [settingsApiClient, projectId]);

  const isLoadingSettings = !settingPC.modifiedAt || !settingReflection.modifiedAt;

  /**
   * Save the settings with a "last writer wins" approach, as specified by PM.
   * Shows an error toast if saving fails.
   * @returns True on success, false on error.
   */
  const applySettings = useCallback(() => {
    return setPublishSettings(projectId, settingsApiClient, handleErrorWithToast,
      { settingPC, settingReflection, settingWorkflowStopAfter }
    );
  }, [settingsApiClient, handleErrorWithToast, projectId, settingPC, settingReflection, settingWorkflowStopAfter]);

  const changeSummary = summarizeRevisionChanges(openDraftEntities);
  const changes = changeSummary?.textJsx.map((change, i) =>
    <li key={i}>{change}</li>
  );
  const shouldShowList = !!changes && 0 < changes.length;

  return (
    <Stack direction={ isScreenXsAndSmall ? "column" : "row" }>
      <Stack sx={{
        backgroundColor: sphereColors.pureWhite,
        width: "100%",
        height: isScreenXsAndSmall ? "400px" : "calc(100vh - 200px)",
        minHeight: "400px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        border: `1px solid ${sphereColors.gray300}`,
        borderRadius: "2px",
      }}>
        <Box sx={{
          fontSize: "32px",
          fontWeight: "600",
          display: "flex",
          justifyContent: "center",
          marginBottom: "30px",
          paddingLeft: "40px",
          paddingRight: "40px",
        }}>
          <Box sx={{
            ...withEllipsisThreeLines,
            lineHeight: "initial",
            textAlign: "center",
          }}>
            Ready to publish!
          </Box>
        </Box>

        <Box sx={{
          fontSize: "16px",
          fontWeight: "400",
          display: "flex",
          justifyContent: "center",
          marginBottom: "10px",
          paddingLeft: "40px",
          paddingRight: "40px",
        }}>
          <Box sx={{
            ...getEllipsisLines({ lines: "5" }),
            lineHeight: "initial",
            textAlign: "center",
          }}>
            Your scans have been successfully registered. You can review the results in the registration tool.
          </Box>
        </Box>
      </Stack>

      <Stack sx={{
        marginLeft: isScreenXsAndSmall ? undefined : "25px",
        width:      isScreenXsAndSmall ? undefined : "400px",
        minWidth:   isScreenXsAndSmall ? undefined : "400px",
        marginTop:  isScreenXsAndSmall ? "20px" : undefined,
        padding:    isScreenXsAndSmall ? "20px" : undefined,
      }}>
        <Box sx={{ fontSize: "20px" }}>
          Choose how your data will be published
        </Box>

        <Box sx={{ mt: "20px" }}>
          <PublishOptions
            workflowType="auto"
            settingPC={settingPC}
            setSettingPC={setSettingPC}
            settingReflection={settingReflection}
            setSettingReflection={setSettingReflection}
          />
        </Box>

        { shouldShowList &&
          <Divider sx={{ marginTop: "20px" }} />
        }

        { shouldShowList &&
          <>
            <Box sx={{ marginTop: "25px", fontWeight: "bold" }}>
              Unpublished changes
            </Box>
            <ul style={{ marginTop: "10px" }}>
              {changes}
            </ul>
          </>
        }

        <Box sx={{
          width: "100%",
          height: "100%",
          marginTop: "40px",
          textAlign: "right",
          alignContent: "end",
        }}>
          <Stack direction="row" sx={{ display: "inline-flex" }}>
            <FaroTextButton
              isDisabled={false}
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </FaroTextButton>

            <PublishButton
              dataTestId="sa-publish-panel-publish"
              projectId={projectId}
              isLoading={isLoadingSettings}
              isDisabled={false}
              isFromPanel={true}
              tooltip="Publish the project."
              sx={{ marginLeft: "10px" }}
              onClickBefore={applySettings}
              onClickAfter={() => setIsOpen(false)}
            />
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
}
